import api from '../services/axiosInstance';
import {
 billingData,
 CardInput,
 Coupon,
 LoginData,
 OrderDetails,
 OrderHistory,
 OrderShow,
 OtpData,
 PageInfo,
 ProductCategory,
 ProductDetailProps,
 ProductsResponse,
 ReturnedOrdersResponse,
 ReturnedOrderDetails,
 SignupData,
 userReview,
 Wishlist,
} from '../interface/interfaces';
import {
 cartItemsAdapter,
 categoriesAdapter,
 OrderShowAdapter,
 productDetailAdapter,
 productsAdapter,
 userReviewsAdapter,
 wishlistAdapter,
} from '../services/adapter';
import axios from 'axios';

class ApiError extends Error {
 status?: number;
 data?: any;

 constructor(message: string, status?: number, data?: any) {
  super(message);
  this.name = 'ApiError';
  this.status = status;
  this.data = data;

  // Required for instanceof to work correctly in transpiled JS
  Object.setPrototypeOf(this, ApiError.prototype);
 }

 toString() {
  return this.message;
 }
}

function errorWrapper(error: any): ApiError {
 if (!error.response) {
  return new Error(error.message);
 }

 let message;

 if (typeof error.response.data.detail === 'string') {
  message = error.response.data.detail;
 } else {
  message = 'An error occurred';
 }

 // const message = error.response.data?.message || "An error occurred";

 const status = error.response.status;
 const data = error.response.data || {};

 console.error('API error:', error.response.data);

 return new ApiError(message, status, data);
}

export async function loginUser(data: LoginData): Promise<any> {
 try {
  const response = await api.post('/auth/login', data);
  return response.data;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function signupUser(data: SignupData): Promise<any> {
 try {
  const response = await api.post('/auth/costumer/register', data);
  return response.data;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function verifyCode(data: OtpData): Promise<any> {
 try {
  let postData = {
   otp: data.code,
  };
  const response = await api.post(
   `/auth/customer/verify/${data.email}`,
   postData,
  );
  return response.data;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function resendCode(email: string): Promise<any> {
 try {
  const response = await api.post(
   `/auth/customer/resend-otp/${email}`,
  );
  return response.data;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function verifyCodeRegular(data: OtpData): Promise<any> {
 try {
  let postData = {
   otp: data.code,
   user_type: 'customer',
  };
  const response = await api.post(
   `/auth/verify-otp?email=${data.email}`,
   postData,
  );
  return response.data;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function sendCodeRegular(email: string): Promise<any> {
 try {
  let body = {
   email: email,
   user_type: 'customer',
  };
  const response = await api.post(`/auth/send-otp`, body);
  return response.data;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function loadBanners(): Promise<any> {
 try {
  const response = await api.get('/banners');
  return response.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadNewArrivals(
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/new-products?page=${page}&size=${size}`,
  );

  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadBestSelling(
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/top-selling-products?page=${page}&size=${size}`,
  );
  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadFlashsales(
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/random-products?page=${page}&size=${size}`,
  );
  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadRecommended(): Promise<ProductsResponse> {
 try {
  const response = await api.get(`/top-rated-products`);

  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function searchProducts(
 query: string,
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/search?query=${query}&page=${page}&size=${size}`,
  );
  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadCategoryProducts(
 category_id: string,
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/category/${category_id}/products?page=${page}&size=${size}`,
  );

  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadSubcategoryProducts(
 subcategory_id: string,
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/sub-category/${subcategory_id}/products?page=${page}&size=${size}`,
  );

  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadRandomProducts(
 page: number,
 size: number,
): Promise<ProductsResponse> {
 try {
  const response = await api.get(
   `/random-products?page=${page}&size=${size}`,
  );

  let resp: ProductsResponse = {
   products: productsAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadCategories(): Promise<ProductCategory[]> {
 try {
  const response = await api.get(`/categories`);

  let resp = categoriesAdapter(response.data);

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadProductDetails(
 product_id: string,
): Promise<ProductDetailProps> {
 try {
  const response = await api.get(`/products/${product_id}`);

  let resp = productDetailAdapter(response.data);

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadUserReviews(
 product_id: string,
 page: number,
 size: number,
): Promise<userReview[]> {
 try {
  const response = await api.get(
   `/product-review/${product_id}?page=${page}&size=${size}`,
  );

  let resp = userReviewsAdapter(response.data.items);

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadCart(
 page: number,
 size: number,
): Promise<any> {
 try {
  type respProp = {
   cartItems: OrderDetails[];
   pageInfo: PageInfo;
  };

  const response = await api.get(
   `/customer/view-cart?page=${page}&size=${size}`,
  );

  let resp: respProp = {
   cartItems: cartItemsAdapter(response.data.items),

   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function addToCartAPi(
 product_id: string,
 size: string,
 color: string,
 quantity: number,
): Promise<boolean> {
 try {
  let body = {
   product_id,
   size,
   color,
   quantity,
  };
  await api.post(`/customer/add-to-cart`, body);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function deleteFromCartApi(
 cart_id: string,
): Promise<boolean> {
 try {
  await api.delete(`/customer/delete-cart/${cart_id}`);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function clearAllCart(): Promise<boolean> {
 try {
  await api.delete(`/customer/delete-all-cart`);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function updateCartApi(
 cart_id: string,
 quantity: number,
 size: string,
 color: string,
): Promise<OrderDetails[]> {
 try {
  let body = {
   quantity,
   size,
   color,
  };

  let response = await api.put(
   `/customer/update-cart/${cart_id}`,
   body,
  );

  return cartItemsAdapter(response.data.cart);
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

type WishlistResponse = {
 wishlist: Wishlist[];
 pageInfo: PageInfo;
};

export async function loadWishlist(
 page: number,
 size: number,
): Promise<WishlistResponse> {
 try {
  const response = await api.get(
   `/customer/wishlist?page=${page}&size=${size}`,
  );

  let resp: WishlistResponse = {
   wishlist: wishlistAdapter(response.data.items),
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function deleteFromWishlist(
 wishlist_id: string,
): Promise<boolean> {
 try {
  await api.delete(`/customer/wishlist/${wishlist_id}`);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function addBillingInfo(
 billingData: any,
): Promise<boolean> {
 try {
  await api.post(`/customer/billing-information`, billingData);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function updateBillingInfo(
 billingData: any,
 billing_id: string,
): Promise<boolean> {
 try {
  await api.patch(
   `/customer/billing-information/${billing_id}`,
   billingData,
  );

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function getBilling(): Promise<billingData[]> {
 try {
  const response = await api.get(`/customer/billing-information`);

  let resp = response.data;

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function deleteBilling(billing_id: string) {
 try {
  await api.delete(`/customer/billing-information/${billing_id}`);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function addCard(
 cardInput: CardInput,
): Promise<boolean> {
 try {
  let obj = {
   card_owner: `${cardInput.firstname} ${cardInput.lastname}`,
   card_number: cardInput.card_number.trim(),
   card_expiry: cardInput.expiry_date,
   card_cvv: cardInput.cvv,
  };

  const response = await api.post(
   `/customer/payment-information`,
   obj,
  );

  let resp = response.data;

  return resp;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadCards() {
 try {
  const response = await api.get(`/customer/payment-information`);

  return response.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function deleteCardApi(card_id: string) {
 try {
  await api.delete(`/customer/payment-information${card_id}`);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function updateCard(
 cardInput: CardInput,
 card_id: string,
): Promise<boolean> {
 try {
  let obj = {
   card_owner: `${cardInput.firstname} ${cardInput.lastname}`,
   card_number: cardInput.card_number.trim(),
   card_expiry: cardInput.expiry_date,
   card_cvv: cardInput.cvv,
  };
  await api.patch(`/customer/payment-information/${card_id}`, obj);

  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function resetPassword(body: any) {
 try {
  await api.post(`/customer/reset-password`, body);
  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function updateProfile(body: any) {
 try {
  await api.patch(`/customer/profile-update`, body);
  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function uploadImage(
 cloud_name: string,
 body: FormData,
) {
 try {
  const res = await axios.post(
   `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
   body,
  );

  return res.data.secure_url;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function checkoutCart(body: any) {
 try {
  let resp = await api.post(`/customer/cart/checkout`, body);

  return resp.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function getCurrentUser() {
 try {
  let resp = await api.get(`/customer/current-user`);

  return resp.data.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadOrders(page: number, size: number) {
 try {
  type respProp = {
   orderHistory: OrderHistory[];
   pageInfo: PageInfo;
  };

  const response = await api.get(
   `/customer/orders?page=${page}&size=${size}`,
  );

  let resp: respProp = {
   orderHistory: response.data.items,
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function getReturnedOrders(page: number, size: number) {
 try {
  const response = await api.get(
   `/customer/returned-orders?page=${page}&size=${size}`,
  );

  return response.data as ReturnedOrdersResponse;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function getReturnedOrderDetails(id: string) {
 try {
  const response = await api.get(`/customer/return-details/${id}`);
  return response.data.data as ReturnedOrderDetails;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function getOrder(order_id: string) {
 try {
  const response = await api.get(`/customer/orders/${order_id}`);

  let result: OrderShow = OrderShowAdapter(response.data);

  return result;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function getCoupon(coupon_id: string) {
 try {
  let resp = await api.get(`/customer/coupons/${coupon_id}`);

  return resp.data as Coupon;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function loadCoupons(page: number, size: number) {
 try {
  type respProp = {
   coupons: Coupon[];
   pageInfo: PageInfo;
  };

  const response = await api.get(
   `/customer/coupons?page=${page}&size=${size}`,
  );

  let resp: respProp = {
   coupons: response.data.items,
   pageInfo: {
    total: response.data.total,
    page: response.data.page,
    size: response.data.size,
    pages: response.data.pages,
   },
  };

  return resp;
 } catch (error) {
  throw errorWrapper(error);
 }
}

export async function getDeliveryFee(billing_id: string) {
 try {
  let body = {
   billing_information: billing_id,
  };

  let resp = await api.post(`/customer/cart/delivery-price`, body);

  return resp.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function addToWishlist(product_id: string) {
 try {
  await api.post(
   `/customer/add-to-wishlist?product_id=${product_id}`,
  );
  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function ResetForgotPassword(obj: any) {
 try {
  let body = {
   new_password: obj.password,
   confirm_password: obj.confirm_password,
   user_type: 'customer',
  };

  await api.post(`/auth/forgot-password?email=${obj.email}`, body);
  return true;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function getOrderAnalytics() {
 try {
  let resp = await api.get(`/customer/order-analytics`);

  return resp.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function returnItem(order_id: string, body: any) {
 try {
  const response = await api.post(
   `/customer/return-item/${order_id}`,
   body,
  );
  return response.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}

export async function getReturnDeliveryFee(
 order_id: string,
 body: any,
) {
 try {
  const response = await api.post(
   `/customer/return/delivery-fee/${order_id}`,
   body,
  );
  return response.data;
 } catch (error: any) {
  throw errorWrapper(error);
 }
}
